import React, { useState, useEffect } from 'react';
import { Camera, MapPin, Mail, Phone, Linkedin, Copy } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Button } from './ui/button';
import { Card } from './ui/card';
import PortfolioAPI from '../services/api';

const Hero = () => {
  const { toast } = useToast();
  const [portfolioData, setPortfolioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileImage, setProfileImage] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Function to copy email to clipboard
  const copyEmail = async () => {
    if (!portfolioData) return;
    const email = portfolioData.personalInfo.email;
    try {
      await navigator.clipboard.writeText(email);
      toast({
        title: "Email Copiado",
        description: `${email} copiado al portapapeles`,
      });
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = email;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast({
        title: "Email Copiado",
        description: `${email} copiado al portapapeles`,
      });
    }
  };

  // Function to copy phone to clipboard
  const copyPhone = async () => {
    if (!portfolioData) return;
    const phone = portfolioData.personalInfo.phone;
    try {
      await navigator.clipboard.writeText(phone);
      toast({
        title: "Teléfono Copiado",
        description: `${phone} copiado al portapapeles`,
      });
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = phone;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast({
        title: "Teléfono Copiado",
        description: `${phone} copiado al portapapeles`,
      });
    }
  };

  // Load portfolio data
  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        const data = await PortfolioAPI.getPortfolio();
        setPortfolioData(data);
        setProfileImage(data.personalInfo.profileImage);
      } catch (error) {
        console.error('Error loading portfolio:', error);
        toast({
          title: "Error",
          description: "No se pudo cargar la información del portafolio",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolio();
  }, [toast]);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploading(true);
      try {
        // Show preview immediately
        const reader = new FileReader();
        reader.onload = (e) => {
          setProfileImage(e.target.result);
        };
        reader.readAsDataURL(file);

        // Upload to server
        const response = await PortfolioAPI.uploadProfileImage(file);
        if (response.success) {
          setProfileImage(response.imageUrl);
          toast({
            title: "Foto Actualizada",
            description: "Tu foto de perfil ha sido actualizada exitosamente",
          });
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        toast({
          title: "Error",
          description: "No se pudo subir la imagen. Inténtalo de nuevo.",
        });
      } finally {
        setUploading(false);
      }
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <section id="hero" className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-600 mx-auto"></div>
          <p className="mt-4 text-stone-600">Cargando portafolio...</p>
        </div>
      </section>
    );
  }

  if (!portfolioData) {
    return (
      <section id="hero" className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-stone-600">Error al cargar el portafolio</p>
        </div>
      </section>
    );
  }

  return (
    <section id="hero" className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src={portfolioData.images.heroBackground}
          alt="Agriculture background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-900/90 via-stone-900/70 to-stone-900/50"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-6">
              <h1 className="text-4xl lg:text-6xl font-bold text-stone-100 leading-tight">
                {portfolioData.personalInfo.name}
              </h1>
              <h2 className="text-xl lg:text-2xl font-semibold text-yellow-600">
                {portfolioData.personalInfo.title}
              </h2>
              <p className="text-lg text-stone-300 leading-relaxed max-w-2xl">
                {portfolioData.personalInfo.tagline}
              </p>
            </div>

            {/* Contact Info */}
            <div className="flex flex-wrap gap-4 text-stone-300">
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-yellow-600" />
                <span className="text-sm">{portfolioData.personalInfo.location}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail size={16} className="text-yellow-600" />
                <button 
                  onClick={copyEmail}
                  className="text-sm hover:text-yellow-600 transition-colors flex items-center gap-1"
                  title="Copiar email"
                >
                  {portfolioData.personalInfo.email}
                  <Copy size={12} className="opacity-70" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <Phone size={16} className="text-yellow-600" />
                <button 
                  onClick={copyPhone}
                  className="text-sm hover:text-yellow-600 transition-colors flex items-center gap-1"
                  title="Copiar teléfono"
                >
                  {portfolioData.personalInfo.phone}
                  <Copy size={12} className="opacity-70" />
                </button>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => scrollToSection('#projects')}
                className="bg-yellow-600 hover:bg-yellow-500 text-stone-900 font-semibold px-8 py-3 text-lg"
              >
                Ver Proyectos
              </Button>
              <Button 
                variant="outline"
                onClick={() => scrollToSection('#contact')}
                className="border-yellow-600 text-yellow-600 hover:bg-yellow-600 hover:text-stone-900 px-8 py-3 text-lg"
              >
                Contactar
              </Button>
            </div>
          </div>

          {/* Profile Image Upload */}
          <div className="flex justify-center lg:justify-end">
            <Card className="relative group bg-stone-800/50 border-stone-700 p-8">
              <div className="relative w-80 h-80 rounded-full overflow-hidden bg-stone-700">
                {profileImage ? (
                  <img 
                    src={profileImage} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-stone-400">
                    <div className="text-center">
                      <Camera size={48} className="mx-auto mb-2" />
                      <p className="text-sm">Foto Profesional</p>
                    </div>
                  </div>
                )}
                
                {/* Upload Overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <label className={`cursor-pointer bg-yellow-600 hover:bg-yellow-500 text-stone-900 px-4 py-2 rounded-full font-medium transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-stone-900 border-t-transparent inline mr-2"></div>
                        Subiendo...
                      </>
                    ) : (
                      <>
                        <Camera size={20} className="inline mr-2" />
                        Subir Foto
                      </>
                    )}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                </div>
              </div>
              
              <div className="mt-6 text-center">
                <p className="text-stone-300 text-sm">
                  Sube tu foto profesional aquí
                </p>
                <div className="mt-3">
                  <a 
                    href={portfolioData.personalInfo.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-yellow-600 hover:text-yellow-500 transition-colors"
                  >
                    <Linkedin size={20} />
                    <span className="text-sm">LinkedIn</span>
                  </a>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;